import { Box, Typography, Paper, Grid, Avatar, LinearProgress, Card, CardContent } from '@mui/material';
import type { ChipColor, ProgressColor } from '@/types/chip';
import { School, CheckCircle, Schedule, HourglassEmpty } from '@mui/icons-material';
import { ONBOARDING_STAGES, STATUS_COLORS } from './constants';

interface OnboardingProgressProps {
  currentStage: string;
  contractType?: string;
  status?: string;
  onAdvanceStage: (stage: string) => void;
}

export const OnboardingProgress = ({ currentStage, contractType, status, onAdvanceStage }: OnboardingProgressProps) => {
  const currentStageIdx = ONBOARDING_STAGES.indexOf(currentStage);
  const onboardingProgress = Math.round(((currentStageIdx + 1) / ONBOARDING_STAGES.length) * 100);

  return (
    <Grid size={{ xs: 12 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <School color="primary" />
            <Typography variant="subtitle1" fontWeight={700}>Onboarding Progress</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">{onboardingProgress}%</Typography>
            <LinearProgress variant="determinate" value={onboardingProgress} sx={{ width: 100, height: 6, borderRadius: 1 }} />
          </Box>
        </Box>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {ONBOARDING_STAGES.map((stage, idx) => {
            const isCompleted = currentStageIdx > idx;
            const isActive = currentStageIdx === idx;
            const isNext = currentStageIdx + 1 === idx;
            return (
              <Grid size={{ xs: 12, sm: 6, md: 12 / ONBOARDING_STAGES.length }} key={stage}>
                <Card
                  variant="outlined"
                  onClick={() => isNext && onAdvanceStage(stage)}
                  sx={{
                    cursor: isNext ? 'pointer' : 'default',
                    height: '100%',
                    borderLeft: '4px solid',
                    borderColor: isCompleted ? 'success.main' : isActive ? 'primary.main' : isNext ? 'warning.main' : 'grey.300',
                    '&:hover': isNext ? { boxShadow: 2 } : {},
                    transition: 'all 0.2s',
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: isCompleted ? 'success.main' : isActive ? 'primary.main' : 'grey.300', fontSize: 14 }}>
                        {isCompleted ? <CheckCircle fontSize="small" /> : isActive ? <Schedule fontSize="small" /> : <HourglassEmpty fontSize="small" />}
                      </Avatar>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {stage.replace(/([A-Z])/g, ' $1').trim()}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {isCompleted ? 'Completed' : isActive ? 'In Progress' : isNext ? 'Click to advance' : 'Pending'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1, textAlign: 'center' }}>
              <Typography variant="h6" fontWeight={700}>{currentStageIdx + 1}/{ONBOARDING_STAGES.length}</Typography>
              <Typography variant="caption" color="text.secondary">Stages Completed</Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1, textAlign: 'center' }}>
              <Typography variant="h6" fontWeight={700}>{contractType?.replace(/([A-Z])/g, ' $1').trim() || '—'}</Typography>
              <Typography variant="caption" color="text.secondary">Contract Type</Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1, textAlign: 'center' }}>
              <Typography variant="h6" fontWeight={700} color={STATUS_COLORS[status ?? ''] as ChipColor}>{status}</Typography>
              <Typography variant="caption" color="text.secondary">Employment Status</Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );
};
